import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle2, Upload, Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import WhatsAppButton from '@/components/WhatsAppButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WarrantyRegistration = () => {
  const [formData, setFormData] = useState({
    customer_name: '',
    mobile_number: '',
    email: '',
    product_name: '',
    model_variant: '',
    purchase_date: ''
  });
  const [invoice, setInvoice] = useState(null);
  const [invoicePreview, setInvoicePreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'Warranty Registration – RSD Accessories';
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('File size must be less than 5MB');
        return;
      }
      setInvoice(file);
      setInvoicePreview(file.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!invoice) {
      toast.error('Please upload invoice document');
      return;
    }

    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('customer_name', formData.customer_name);
      formDataToSend.append('mobile_number', formData.mobile_number);
      if (formData.email) formDataToSend.append('email', formData.email);
      formDataToSend.append('product_name', formData.product_name);
      formDataToSend.append('model_variant', formData.model_variant);
      formDataToSend.append('purchase_date', formData.purchase_date);
      formDataToSend.append('invoice', invoice);

      await axios.post(`${API}/warranty-registration`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSubmitted(true);
      toast.success('Warranty registered successfully!');
      
      // Reset form
      setFormData({
        customer_name: '',
        mobile_number: '',
        email: '',
        product_name: '',
        model_variant: '',
        purchase_date: ''
      });
      setInvoice(null);
      setInvoicePreview('');
    } catch (error) {
      console.error('Error submitting warranty registration:', error);
      toast.error('Failed to register warranty. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-black to-neutral-900 text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#FF4D00] rounded-full mb-6">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl md:text-7xl font-black mb-6">
            Warranty{' '}
            <span className="text-[#FF4D00]">Registration</span>
          </h1>
          <p className="text-xl md:text-2xl text-neutral-300 max-w-3xl mx-auto">
            Register your RSD product and activate your warranty coverage
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm text-center">
              <div className="text-4xl mb-3">🛡️</div>
              <h3 className="font-bold text-lg mb-2">Extended Protection</h3>
              <p className="text-neutral-600 text-sm">Get additional 30 days extended warranty on registration</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm text-center">
              <div className="text-4xl mb-3">⚡</div>
              <h3 className="font-bold text-lg mb-2">Quick Service</h3>
              <p className="text-neutral-600 text-sm">Faster claim processing with registered products</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm text-center">
              <div className="text-4xl mb-3">📧</div>
              <h3 className="font-bold text-lg mb-2">Email Confirmation</h3>
              <p className="text-neutral-600 text-sm">Instant email confirmation on successful registration</p>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Register Your Product</h2>
            <p className="text-lg text-neutral-600">
              Fill in the details below to activate your warranty
            </p>
          </div>

          {submitted && (
            <div data-testid="success-message" className="mb-8 bg-green-50 border border-green-200 rounded-xl p-6 flex items-center gap-4">
              <CheckCircle2 className="w-8 h-8 text-green-600 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-green-900 mb-1">Warranty Registered Successfully!</h3>
                <p className="text-green-700">You will receive a confirmation email at {formData.email || 'your registered email'} shortly.</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="warranty-form" className="bg-neutral-50 rounded-2xl p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="customer_name" className="text-sm font-semibold mb-2 block">
                  Customer Name *
                </Label>
                <Input
                  id="customer_name"
                  name="customer_name"
                  data-testid="name-input"
                  type="text"
                  required
                  value={formData.customer_name}
                  onChange={handleChange}
                  className="h-12 rounded-lg"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <Label htmlFor="mobile_number" className="text-sm font-semibold mb-2 block">
                  Mobile Number *
                </Label>
                <Input
                  id="mobile_number"
                  name="mobile_number"
                  data-testid="mobile-input"
                  type="tel"
                  required
                  value={formData.mobile_number}
                  onChange={handleChange}
                  className="h-12 rounded-lg"
                  placeholder="Enter mobile number"
                  pattern="[0-9]{10}"
                  title="Please enter a valid 10-digit mobile number"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-semibold mb-2 block">
                Email ID (Optional)
              </Label>
              <Input
                id="email"
                name="email"
                data-testid="email-input"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="h-12 rounded-lg"
                placeholder="Enter email address"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="product_name" className="text-sm font-semibold mb-2 block">
                  Product Name *
                </Label>
                <Input
                  id="product_name"
                  name="product_name"
                  data-testid="product-input"
                  type="text"
                  required
                  value={formData.product_name}
                  onChange={handleChange}
                  className="h-12 rounded-lg"
                  placeholder="e.g., RSD SONIC PRO Earbuds"
                />
              </div>

              <div>
                <Label htmlFor="model_variant" className="text-sm font-semibold mb-2 block">
                  Model / Variant *
                </Label>
                <Input
                  id="model_variant"
                  name="model_variant"
                  data-testid="model-input"
                  type="text"
                  required
                  value={formData.model_variant}
                  onChange={handleChange}
                  className="h-12 rounded-lg"
                  placeholder="e.g., Black / 65W"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="purchase_date" className="text-sm font-semibold mb-2 block">
                Purchase Date *
              </Label>
              <Input
                id="purchase_date"
                name="purchase_date"
                data-testid="date-input"
                type="date"
                required
                value={formData.purchase_date}
                onChange={handleChange}
                className="h-12 rounded-lg"
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div>
              <Label htmlFor="invoice" className="text-sm font-semibold mb-2 block">
                Upload Invoice *
              </Label>
              <div className="mt-2">
                <label
                  htmlFor="invoice"
                  className="flex items-center justify-center w-full h-32 border-2 border-dashed border-neutral-300 rounded-lg cursor-pointer hover:border-[#FF4D00] transition-colors bg-white"
                >
                  <div className="text-center">
                    <Upload className="w-8 h-8 mx-auto mb-2 text-neutral-400" />
                    {invoicePreview ? (
                      <p className="text-sm font-medium text-[#FF4D00]">
                        📄 {invoicePreview}
                      </p>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-neutral-600">Click to upload invoice</p>
                        <p className="text-xs text-neutral-500 mt-1">PDF, JPG, PNG (Max 5MB)</p>
                      </>
                    )}
                  </div>
                  <input
                    id="invoice"
                    name="invoice"
                    data-testid="invoice-input"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="hidden"
                    required
                  />
                </label>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Please ensure that the invoice clearly shows the product name, purchase date, and seller information. Incomplete or unclear invoices may delay warranty activation.
              </p>
            </div>

            <Button
              type="submit"
              data-testid="submit-button"
              disabled={submitting}
              className="w-full bg-[#FF4D00] hover:bg-[#E04400] text-white rounded-full py-6 text-lg font-bold disabled:opacity-50"
            >
              {submitting ? 'Registering...' : 'Register Warranty'}
            </Button>
          </form>
        </div>
      </section>

      <WhatsAppButton />
    </div>
  );
};

export default WarrantyRegistration;
